import {
  ADDITIONAL_SCALING_FACTOR,
  DieEventTypes,
  DieTypes,
  DISPOSE_LENGTH,
  isDebug,
  PddPayloadTypes,
} from "@/constants";
import { dieConfigurations } from "@/constants/diceConfig";
import { Context } from "@/context";
import { DieEventValues } from "@/enums";
import { toEulerFromDegrees } from "@/helpers/toEulerFromDegrees";
import { useRenderedDiceStore } from "@/hooks/useRenderedDiceStore";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import type { MaterialResponse } from "@/types";
import { invalidate, useFrame } from "@react-three/fiber";
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
} from "react";
import {
  MeshPhysicalMaterial,
  Vector3,
  type Mesh,
  type WebGLProgramParametersWithUniforms,
} from "three";
import { Node } from "./components/Node";
import { ParticleSystem } from "./components/ParticleSystem";
import { addShadow } from "./helpers/addShadow";
import { getTextureAsSrgb } from "./helpers/getTextureAsSrgb";
import { processDiceTextures } from "./helpers/processDiceTextures";
import { updateShader } from "./helpers/updateShader";
import { useAnimatedFrameData } from "./hooks/useAnimatedFrameData";
import { useParticleSystemsHandler } from "./hooks/useParticleSystemsHandler";
import { useSoundEventHandler } from "./hooks/useSoundEventHandler";
import { useTextures } from "./hooks/useTextures";

interface RenderedDieProps {
  id: string;
  preview?: boolean;
  rotation?: [number, number, number];
}

export const RenderedDie: FC<RenderedDieProps> = ({
  id,
  preview,
  rotation,
}) => {
  const { lastStandardEvent, rollId, upsideValue, dieType, offset } =
    useRenderedDiceStore((state) => state.renderedDice.entities[id]);
  const { message } = useRenderedDiceStore(
    (state) => state.rolls.entities[rollId],
  );
  const setId = message.data.setId;
  const manifest = useRenderedDiceStore(
    (state) => state.manifests.entities[setId],
  );
  const { user: u, renderedWorkerPort } = useContext(Context);
  const messenger: Pick<typeof self, "postMessage"> =
    renderedWorkerPort ?? self;
  const user = u || (self.name ? JSON.parse(self.name).user : undefined);
  const [readyNodes, setReadyNodes] = useState(new Set<string>());
  const { setCurrentEvent, clearRenderedRoll } = useRenderedDiceStore();
  const onNodeReady = useCallback(
    (nodeName: string) =>
      setReadyNodes((prev) =>
        prev.has(nodeName) ? prev : new Set(prev).add(nodeName),
      ),
    [],
  );
  const previewIsParticlesEnabled = useUserDiceDataStore(
    (state) => state.getUserDiceData(user.id)?.settings.particlesEnabled,
  );
  const renderedIsParticlesEnabled = useRenderedDiceStore(
    (state) => state.userSettings?.particlesEnabled,
  );
  const isParticlesEnabled = preview
    ? previewIsParticlesEnabled
    : renderedIsParticlesEnabled;
  // TODO: Handle `stackSiblings` for Dice of Many Things Cards once we have billboarding working
  const siblingNodes = manifest?.[dieType].siblingNodes?.map((name) => (
    <Node
      key={name}
      name={name}
      id={id}
      preview={preview}
      onReady={onNodeReady}
    />
  ));
  // Total isn't defined until manifest is loaded
  const totalNodeCount = manifest ? 1 + (siblingNodes?.length ?? 0) : undefined;
  const areAllNodesReady = manifest && readyNodes.size === totalNodeCount;
  const { positionGroupRef, quaternionGroupRef, currentFrameRef } =
    useAnimatedFrameData(
      id,
      areAllNodesReady,
      () => {
        setCurrentEvent(id, {
          trigger: DieEventTypes.ROLL_BEGIN,
        });
      },
      () => {
        setCurrentEvent(
          id,
          {
            trigger: DieEventTypes.ROLL_COMPLETE,
          },
          undefined,
          user.id,
          (message) => messenger.postMessage(message),
        );
        // TODO: This may need revisiting depending on if there's a more correct
        // way to handle rollComplete and max/min roll events.
        // But it seems in OG dice they are called right around the same time.
        setTimeout(() => {
          if (upsideValue === 1 || upsideValue === values) {
            setCurrentEvent(id, {
              trigger:
                upsideValue === 1
                  ? DieEventTypes.MIN_ROLL
                  : DieEventTypes.MAX_ROLL,
            });
          }
        }, 50);
      },
      preview,
    );
  const {
    materials,
    animations: manifestAnimations,
    scale: manifestScale,
  } = manifest[dieType];
  const textures = useTextures(manifest.slug, manifest.resources.textures);
  const depthShaderRef = useRef<WebGLProgramParametersWithUniforms>(null);
  const shadersRef = useRef<Record<string, WebGLProgramParametersWithUniforms>>(
    {},
  );
  const globalTime = useRef(0);
  const disposeTime = useRef(0);
  const { meshSizeMultiplier, values } = dieConfigurations[dieType];
  const scale =
    meshSizeMultiplier * (manifestScale ?? 1.0) * ADDITIONAL_SCALING_FACTOR;
  const firstMaterial = Object.values(materials)?.[0] as MaterialResponse;
  const currentUseDissolveValue = Boolean(
    firstMaterial?.properties?.dissolveTexture ||
    (!manifestAnimations && !firstMaterial?.properties?.dissolveTexture),
  );
  const currentUseNumbersValues = useRef<Record<string, boolean>>({});
  const initDate = useRef<number | null>(null);

  useEffect(() => {
    if (preview) return;

    if (initDate.current == null) initDate.current = Date.now();

    const emergencyDispose = setTimeout(() => {
      const startedAt = initDate.current ?? Date.now();

      if (startedAt + 30_000 < Date.now()) {
        clearRenderedRoll(rollId);
        messenger.postMessage({
          type: PddPayloadTypes.RemoveRoll,
          payload: rollId,
        });
        invalidate();
      }
    }, 30_000);

    return () => clearTimeout(emergencyDispose);
  }, [clearRenderedRoll, messenger, preview, rollId]);

  useSoundEventHandler(id, currentFrameRef, manifest);

  const activeParticleSystems = useParticleSystemsHandler(
    id,
    manifest[dieType].particleSystems,
    preview,
  );

  useEffect(() => {
    const shaders = Object.entries(shadersRef.current);

    if (!depthShaderRef.current) {
      return;
    }

    if (isDebug || preview) {
      console.log(`Die event for id ${id}: ${lastStandardEvent ?? "none"}`);
    }

    shaders.forEach(
      ([, shader]) => (shader.uniforms.lastEvent.value = lastStandardEvent),
    );

    depthShaderRef.current.uniforms.lastEvent.value = lastStandardEvent;

    shaders.forEach(
      ([, shader]) =>
        (shader.uniforms.lastEventTime.value = globalTime.current),
    );

    depthShaderRef.current.uniforms.lastEventTime.value = globalTime.current;

    if (lastStandardEvent === DieEventValues.rollComplete) {
      const dieState =
        useRenderedDiceStore.getState().renderedDice.entities[id];

      let dieValue = dieState.upsideValue ?? 0;
      if (dieState.dieType === DieTypes.d100) {
        dieValue = dieValue === 0 ? 10 : dieValue / 10;
      }

      shaders.forEach(
        ([, shader]) => (shader.uniforms.dieValue.value = dieValue),
      );

      shaders.forEach(([materialName, shader]) => {
        if (!currentUseNumbersValues.current[materialName]) return;

        shader.uniforms.topFaceNormal.value = new Vector3(
          dieState.closestNormal?.[0] ?? 0,
          dieState.closestNormal?.[1] ?? 0,
          dieState.closestNormal?.[2] ?? 0,
        );
      });
    }

    if (lastStandardEvent === DieEventValues.dieRemoved) {
      disposeTime.current = Date.now() + 1_000 * DISPOSE_LENGTH;
    }

    invalidate();
  }, [lastStandardEvent, id, preview]);

  useEffect(() => {
    // Only start showing the die once rollBegin has hit
    // to ensure initial animations have already started
    if (preview || lastStandardEvent === DieEventValues.rollBegin) {
      positionGroupRef.current.visible = true;
    }
  }, [lastStandardEvent, positionGroupRef, preview]);

  useFrame((_, delta) => {
    if (lastStandardEvent === undefined) return;

    const shaders = Object.values(shadersRef.current);

    globalTime.current += delta;

    shaders.forEach(
      (shader) => (shader.uniforms.iGlobalTime.value = globalTime.current),
    );

    if (depthShaderRef.current) {
      depthShaderRef.current.uniforms.iGlobalTime.value = globalTime.current;
    }

    if (
      currentUseDissolveValue &&
      lastStandardEvent === DieEventValues.dieRemoved
    ) {
      const timeTillDispose = 0.001 * (disposeTime.current - Date.now());
      const dissolveValue = DISPOSE_LENGTH - timeTillDispose;

      shaders.forEach(
        (shader) => (shader.uniforms.dissolveValue.value = dissolveValue),
      );

      if (depthShaderRef.current) {
        depthShaderRef.current.uniforms.dissolveValue.value = dissolveValue;
      }
    }

    invalidate();
  });

  const particleSystems = isParticlesEnabled
    ? Object.entries(manifest.particleSystems ?? {}).map(([name, psConfig]) => {
        const meta = manifest.resources.particleSystems?.[name];
        const texture = getTextureAsSrgb(textures[meta.particleTexture]);
        const shaderName = psConfig.customShader?.shaderPath.fragmentElement;
        const customShader = shaderName
          ? manifest.shaders?.[shaderName]
          : undefined;
        const diffuseTexture = customShader && texture;
        const maskTextureName = meta.uniforms?.maskTexture?.value;
        const maskTexture = maskTextureName
          ? textures[maskTextureName]
          : undefined;
        return (
          <ParticleSystem
            key={name}
            particleSystem={psConfig}
            texture={texture}
            particleShader={customShader}
            diffuseTexture={diffuseTexture}
            maskTexture={maskTexture}
            isActive={activeParticleSystems.has(name)}
          />
        );
      })
    : undefined;

  const onMaterialCreated = (mesh: Mesh) => {
    const material = mesh.material as MeshPhysicalMaterial;

    // we don't need any of this for custom materials
    if (!material.isMeshPhysicalMaterial || material.userData.isCustomMaterial)
      return;

    const materialName = material.name === "" ? "default" : material.name;

    const manifestMaterial =
      typeof materials[materialName] === "string"
        ? manifest.resources.materials[materials[materialName]]
        : materials[materialName];
    const props = manifestMaterial?.properties;

    currentUseNumbersValues.current[materialName] =
      props?.useNumbers ?? Boolean(props?.numberTexture);
    // set up tangents for mesh geometry that use the glitter shaders
    const geometry = mesh.geometry;

    if (geometry && !geometry.getAttribute("tangent") && props?.glitterScale) {
      geometry.computeTangents();
    }

    const { dissolveTexture, numberTexture, glitterPatternTexture } =
      processDiceTextures(textures, props);

    material.onBeforeCompile = (shader) => {
      updateShader(
        shader,
        currentUseNumbersValues.current[materialName],
        values,
        dissolveTexture,
        numberTexture,
        currentUseDissolveValue,
        props?.numberColor,
        props?.numberHighlightColor,
        props?.numberBumpScale,
        props?.numbersMode,
        props?.marbleAmplitude,
        props?.marbleTiming,
        props?.marbleZoom,
        props?.glitterScale,
        glitterPatternTexture,
        props?.glitterNoiseTexture
          ? textures[props.glitterNoiseTexture]
          : undefined,
      );
      addShadow(mesh, currentUseDissolveValue, dissolveTexture, depthShaderRef);

      shadersRef.current[materialName] = shader;
    };
  };
  const previewRotation = toEulerFromDegrees(
    rotation?.[0] ?? offset?.[0] ?? 0,
    rotation?.[1] ?? offset?.[1] ?? 0,
    rotation?.[2] ?? offset?.[2] ?? 0,
    "YXZ",
  );

  return (
    <group ref={positionGroupRef} scale={scale} visible={false}>
      <group ref={quaternionGroupRef}>
        <group rotation={previewRotation}>
          <Node
            id={id}
            preview={preview}
            onMaterialCreated={onMaterialCreated}
            onReady={onNodeReady}
            name={dieType}
          />
          {siblingNodes}
          {particleSystems}
        </group>
      </group>
    </group>
  );
};
